// personale-api.js — amm-v3 (A4, ondata 31): parte PURA della vista "Costo
// personale" (pagina 'personale' — matrice dipendente×mese dai cedolini +
// quadratura cedolini↔F24), testabile sotto node (contract test
// server/src/lib/amm-v3-personale.test.js).
//
// PRINCIPI (rito A1/A2/A3/proc-v3):
//  - TESTI VERBATIM dal v1 (admin-module.js, renderPersonale r.2551-2712):
//    pinnati dal contract test.
//  - Le FORMULE replicate come funzioni pure — filtro mesi con dati (r.2569),
//    somma "Totali azienda" per mese (r.2607-2620), delta di quadratura
//    (r.2650-2655). NB: le celle del costo (lorda/irpef/...) arrivano GIÀ
//    calcolate dal server (costoPersonale in amm-personale.js): il v3 NON
//    ricalcola, le mostra (parità di fonte).
//  - STATO CONDIVISO col v1: anno su AMM.personaleAnno (r.750), toggle azienda
//    su AMM.personaleVistaAzienda (r.2665) — flip di flag senza perdite.
//  - DELEGHE: helpIcon (HTML ⓘ), personaleStatoBadge (HTML badge), openCedolino
//    (modale cedolino), fmtEur2, toast. ZERO bypass di render (censiti: 3
//    handler interni toggle/prev/next, dal DOM v1 che nel mondo v3 non esiste)
//    ⇒ NIENTE interceptor (come A3).
//  - MAI 'nav:badge' o canali nuovi da client/src/amm-v3/**.

import { apiPathFn } from './api.js';

export const AP_DELEGATED_METHODS = [
  'renderPage',            // il seam stesso (wrap) + refresh post-azione
  'renderPersonale',       // la vista v1 wrappata (drift guard: rename ⇒ rosso)
  'fmtEur2',               // € a 2 decimali (unica fonte: v1)
  'toast',                 // esito export / "Cedolino non trovato"
  'helpIcon',              // ⓘ del titolo (HTML v1, delega document-level)
  'personaleStatoBadge',   // badge stato quadratura (HTML v1, iniettato verbatim)
  'openCedolino',          // modale cedolino (click riga mese) — resta v1
];

export const AP_API_PATHS = [
  'personale.costi',       // GET /personale/costi?anno=
  'personale.quadratura',  // GET /personale/quadratura?anno=
  'buste.listForDip',      // GET /dipendenti/:id/buste (per aprire il cedolino del mese)
  'export.download',       // Esporta CSV (vista 'personale')
];

// ---- TESTI VERBATIM dal v1 (pinnati dal contract test) ----
export const AP_TEXTS = {
  title: 'Costo personale',
  helpKey: 'cassa_edile_riepilogo',   // r.2667: helpIcon del titolo
  sub: 'costo del lavoro dai cedolini · quadratura con gli F24 versati',
  toggleOff: 'Totali azienda',
  toggleOn: '✓ Totali azienda',
  exportCsv: 'Esporta CSV',
  kpiCostoTot: 'Costo totale ',       // + anno
  kpiNetto: 'Netto pagato',
  kpiContrDitta: 'Contributi ditta',
  kpiTfr: 'TFR',
  colMese: 'Mese', colLorda: 'Lorda', colIrpef: 'IRPEF', colContrDip: 'Contr. dip.',
  colContrDitta: 'Contr. ditta', colTfr: 'TFR', colNetto: 'Netto', colCostoAz: 'Costo azienda',
  rigaTotale: 'Totale',
  mancante: 'mancante',
  mancanteTitleSingola: 'Nessun cedolino registrato per questo mese, atteso in base al periodo di lavoro',
  mancanteTitleAgg: 'Numero di dipendenti senza cedolino registrato per questo mese, atteso in base al periodo di lavoro',
  aggTitoloPre: 'Totale mensile — tutti i dipendenti (', // + N + ')'
  aggEmptyPre: 'Nessun movimento aggregato nel ',        // + anno + '.'
  emptyDip: 'Nessun dipendente con cedolini nel ',       // + anno + '.'
  quadTitolo: 'Quadratura versamenti',
  quadColMese: 'Mese',
  quadColRitenute: 'Ritenute (cedolini / F24)',
  quadColInps: 'INPS (cedolini / F24)',
  quadNota: 'Il riferimento è il mese di competenza; il versamento avviene il 16 del mese successivo.',
  cedolinoNonTrovato: 'Cedolino non trovato',
  exportFallito: 'Export fallito: ',
};

// mesi brevi capitalizzati — VERBATIM v1 r.2553 (colonna Mese)
export const MESI_S = ['Gen', 'Feb', 'Mar', 'Apr', 'Mag', 'Giu', 'Lug', 'Ago', 'Set', 'Ott', 'Nov', 'Dic'];

// chiavi sommabili — VERBATIM v1 r.2606 (Totali azienda)
export const CHIAVI_SOMMA = ['lorda', 'irpef', 'contributi_dip', 'contributi_ditta', 'tfr', 'netto', 'costo_azienda'];

// ---- STATO condiviso col v1 ----
export function personaleStato(A) {
  if (!A.personaleAnno) A.personaleAnno = new Date().getFullYear();
  return { anno: A.personaleAnno, vistaAzienda: !!A.personaleVistaAzienda };
}

// ---- FORMULE v1 replicate (pure) ----

// mesi del dipendente da MOSTRARE: solo quelli con un dato (r.2569) — mai righe
// tutte-zero. Un mese 'mancante' resta mostrato (è un segnale, non un vuoto).
export function mesiConDati(dip) {
  return (dip.mesi || []).filter((m) => m.lorda || m.irpef || m.contributi_dip || m.contributi_ditta || m.tfr || m.netto || m.costo_azienda || m.mancante);
}

// "Totali azienda": somma CLIENT per mese delle colonne già ricevute
// (r.2607-2620) — zero formule server nuove. Restituisce SOLO i mesi con dati.
export function sommaTotaliAzienda(dipendenti) {
  const perMese = [];
  for (let mi = 0; mi < 12; mi++) {
    const agg = { mese: mi + 1, lorda: 0, irpef: 0, contributi_dip: 0, contributi_ditta: 0, tfr: 0, netto: 0, costo_azienda: 0, mancanti: 0 };
    (dipendenti || []).forEach((dip) => {
      const m = dip.mesi[mi];
      if (!m) return;
      CHIAVI_SOMMA.forEach((k) => { agg[k] += Number(m[k]) || 0; });
      if (m.mancante) agg.mancanti++;
    });
    CHIAVI_SOMMA.forEach((k) => { agg[k] = Math.round(agg[k] * 100) / 100; });
    perMese.push(agg);
  }
  return perMese.filter((a) => a.lorda || a.irpef || a.contributi_dip || a.contributi_ditta || a.tfr || a.netto || a.costo_azienda || a.mancanti);
}

// delta di quadratura (r.2655): (cedolini - f24) arrotondato a 2 decimali
export function round2diff(a, b) { return Math.round(((Number(a) || 0) - (Number(b) || 0)) * 100) / 100; }

// etichetta badge "N mancante/i" della riga aggregata (r.2624)
export function mancantiLabel(n) { return n + ' ' + AP_TEXTS.mancante + (n > 1 ? 'i' : ''); }

// ---- testi calcolati ----
export function aggTitolo(n) { return AP_TEXTS.aggTitoloPre + n + ')'; }
export function aggEmpty(anno) { return AP_TEXTS.aggEmptyPre + anno + '.'; }
export function emptyDip(anno) { return AP_TEXTS.emptyDip + anno + '.'; }

// ---- LETTURA dati (default v1 r.2554-2556) ----
export async function readPersonale(win, anno) {
  const [costiRaw, quadRaw] = await Promise.all([
    apiPathFn(win, 'personale.costi').call(null, anno),
    apiPathFn(win, 'personale.quadratura').call(null, anno),
  ]);
  const costi = costiRaw || { dipendenti: [], totali: {} };
  return {
    dipendenti: costi.dipendenti || [],
    totali: costi.totali || {},
    quadratura: quadRaw || [],
  };
}

// ---- AZIONI e DELEGHE (stessi punti del v1 r.2701-2711) ----
// Click su un mese: trova il cedolino del dipendente per anno/mese e lo apre
// (openCedolino v1). Se non c'è → toast "Cedolino non trovato" (parità r.2707).
export async function apriCedolinoMese(win, dipId, mese, anno) {
  const A = win.AMM;
  try {
    const buste = await apiPathFn(win, 'buste.listForDip').call(null, dipId);
    const b = (buste || []).find((x) => Number(x.anno) === Number(anno) && Number(x.mese) === Number(mese));
    if (!b) { A.toast(AP_TEXTS.cedolinoNonTrovato); return; }
    A.openCedolino(b.id);
  } catch (e) { A.toast(AP_TEXTS.cedolinoNonTrovato); }
}

export function esportaCsvPersonale(win, anno) { return apiPathFn(win, 'export.download').call(null, 'personale', anno); }
